import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Send, RotateCcw } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const PERSONAS = {
  tuahine: {
    label: "Tuahine",
    sub: "sister from inside",
    intro: "Warm, peer, real. She's been there.",
    primary: "#9C6644",
    bg: "#F6E9D8",
    bubble: "#9C6644",
    greeting: "Kia ora sis. I'm here. What's going on for you today?",
  },
  whaea: {
    label: "Whaea",
    sub: "kuia · kaupapa-driven",
    intro: "Stern, no-frills, your best interest. She tells you the truth.",
    primary: "#2B4522",
    bg: "#DDE5D5",
    bubble: "#2B4522",
    greeting: "Kia ora e hine. Tell me what's actually going on. We'll work it through.",
  },
};

export default function Companion() {
  const { user } = useAuth();
  const [persona, setPersona] = useState(localStorage.getItem("mmko_persona") || "tuahine");
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef();

  useEffect(() => {
    localStorage.setItem("mmko_persona", persona);
    api.get(`/companion/messages?persona=${persona}`).then((r) => {
      setMessages(r.data || []);
    }).catch(() => setMessages([]));
  }, [persona]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, sending]);

  const send = async (e) => {
    e?.preventDefault();
    const text = draft.trim();
    if (!text || sending) return;
    setDraft("");
    setSending(true);
    // optimistic user message
    const tempUser = { id: `tmp-${Date.now()}`, role: "user", content: text };
    setMessages((p) => [...p, tempUser]);
    try {
      const { data } = await api.post("/companion/chat", { persona, message: text });
      setMessages((p) => [
        ...p.filter((m) => m.id !== tempUser.id),
        data.user_message,
        data.assistant_message,
      ]);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "She's resting — try again");
      setMessages((p) => p.filter((m) => m.id !== tempUser.id));
      setDraft(text);
    } finally {
      setSending(false);
    }
  };

  const clear = async () => {
    if (!window.confirm("Clear this kōrero?")) return;
    await api.delete(`/companion/messages?persona=${persona}`);
    setMessages([]);
  };

  const p = PERSONAS[persona];
  const showGreeting = messages.length === 0;

  return (
    <div className="animate-fadeup -mx-4 sm:mx-0" style={{ minHeight: "calc(100vh - 12rem)" }}>
      <div
        className="rounded-none sm:rounded-3xl border border-border overflow-hidden flex flex-col"
        style={{ backgroundColor: p.bg, height: "calc(100vh - 14rem)" }}
      >
        {/* Persona switcher */}
        <div className="sticky top-0 bg-muka/80 backdrop-blur border-b border-border p-3 sm:p-4 flex items-center justify-between gap-3 z-10">
          <div className="flex gap-1 bg-bone rounded-full p-1 border border-border">
            {Object.entries(PERSONAS).map(([k, pp]) => (
              <button
                key={k}
                onClick={() => setPersona(k)}
                data-testid={`persona-${k}`}
                className={`px-4 sm:px-5 py-2 rounded-full text-sm transition-all ${
                  persona === k ? "text-muka" : "text-charcoal/70"
                }`}
                style={persona === k ? { backgroundColor: pp.primary } : {}}
              >
                <span className="font-serif">{pp.label}</span>
                <span className="hidden sm:inline opacity-70 text-xs ml-2">· {pp.sub}</span>
              </button>
            ))}
          </div>
          <button
            onClick={clear}
            data-testid="companion-clear"
            className="text-xs text-muted-foreground hover:text-charcoal flex items-center gap-1"
            aria-label="Clear conversation"
          >
            <RotateCcw size={12} /> clear
          </button>
        </div>

        {/* Messages */}
        <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-4">
          {showGreeting && (
            <div className="text-center py-8 sm:py-12 max-w-md mx-auto">
              <div
                className="w-20 h-20 rounded-full mx-auto mb-4 flex items-center justify-center font-serif text-3xl text-muka"
                style={{ backgroundColor: p.primary }}
                data-testid="companion-avatar"
              >
                {p.label[0]}
              </div>
              <div className="font-serif text-2xl text-charcoal mb-1">{p.label}</div>
              <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-4">{p.sub}</div>
              <p className="text-charcoal/70 italic mb-4">{p.intro}</p>
              <p className="text-charcoal/80">{p.greeting}</p>
            </div>
          )}

          {messages.map((m) => (
            <Bubble key={m.id} msg={m} persona={p} userName={user?.display_name} />
          ))}
          {sending && <Typing persona={p} />}
        </div>

        {/* Composer */}
        <form onSubmit={send} className="border-t border-border p-3 sm:p-4 bg-muka/60">
          <div className="flex items-end gap-2">
            <textarea
              rows={1}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  send();
                }
              }}
              placeholder={`Talk to ${p.label}...`}
              data-testid="companion-input"
              className="flex-1 px-4 py-3 rounded-2xl border border-border bg-muka focus:outline-none focus:ring-2 resize-none max-h-32"
              style={{ "--tw-ring-color": p.primary }}
            />
            <button
              type="submit"
              disabled={sending || !draft.trim()}
              data-testid="companion-send"
              className="w-12 h-12 rounded-full text-muka flex items-center justify-center disabled:opacity-40 transition-transform hover:scale-105"
              style={{ backgroundColor: p.primary }}
              aria-label="Send"
            >
              <Send size={18} />
            </button>
          </div>
          <div className="text-[10px] text-muted-foreground mt-2 text-center">
            {p.label} is not a crisis service. If you are unsafe, tap the SOS button.
          </div>
        </form>
      </div>
    </div>
  );
}

function Bubble({ msg, persona, userName }) {
  const isUser = msg.role === "user";
  return (
    <div
      className={`flex ${isUser ? "justify-end" : "justify-start"} animate-fadeup`}
      data-testid={isUser ? "chat-message-user" : "chat-message-assistant"}
    >
      <div className={`max-w-[80%] sm:max-w-[70%] flex flex-col ${isUser ? "items-end" : "items-start"}`}>
        {!isUser && (
          <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mb-1">
            {persona.label}
          </div>
        )}
        <div
          className={`px-4 py-3 rounded-2xl whitespace-pre-wrap leading-relaxed ${
            isUser ? "rounded-br-md text-muka" : "rounded-bl-md bg-muka text-charcoal border border-border"
          }`}
          style={isUser ? { backgroundColor: persona.bubble } : {}}
        >
          {msg.content}
        </div>
      </div>
    </div>
  );
}

function Typing({ persona }) {
  return (
    <div className="flex justify-start animate-fadeup">
      <div className="flex flex-col items-start">
        <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mb-1">{persona.label}</div>
        <div className="px-4 py-3 rounded-2xl rounded-bl-md bg-muka border border-border flex gap-1.5">
          <Dot delay="0s" /><Dot delay="0.15s" /><Dot delay="0.3s" />
        </div>
      </div>
    </div>
  );
}

function Dot({ delay }) {
  return (
    <span
      className="w-2 h-2 rounded-full bg-charcoal/40 inline-block animate-bounce"
      style={{ animationDelay: delay }}
    />
  );
}
